import { FC, useRef } from "react";
import { FlowDurationModel } from "../../models/FlowDurationModel";

function normalize(value: string): string {
  if (value.length === 0) {
    return "00";
  } else if (value.length === 1) {
    return `0${value}`;
  }
  return value.toString();
}

type FlowTimesModel = {
  times: {
    original: string;
    current: string;
  };
  sla_adjustment: string;
};

type FlowTimesPanelProps = {
  data: FlowTimesModel;
  onUpdate: (data: FlowTimesModel) => void;
};

export const FlowTimesPanel: FC<FlowTimesPanelProps> = (props) => {
  return (
    <div className="flex flex-1 flex-col p-4 gap-2">
      <div className="flex flex-col gap-4">
        <FlowTimeLine
          label="Opprinnelig: "
          value={props.data.times.original || "00:00:00"}
          onChange={(value) =>
            props.onUpdate({
              ...props.data,
              times: { ...props.data.times, original: value },
            })
          }
        />
        <FlowTimeLine
          label="Nåværende:"
          value={props.data.times.current || "00:00:00"}
          onChange={(value) =>
            props.onUpdate({
              ...props.data,
              times: { ...props.data.times, current: value },
            })
          }
        />
        <div className="flex items-center mt-16">
          <SlaTimeInput
            value={
              props.data.sla_adjustment
                ? parseInt(props.data.sla_adjustment)
                : 0
            }
            onChange={(time) => {
              props.onUpdate({
                ...props.data,
                sla_adjustment: time.toString(),
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};

const DurationLine: FC<{ item: FlowDurationModel }> = (props) => {
  const time: { hours: number; minutes: number; seconds: number } = ((
    time: string
  ) => {
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    if (time) {
      const timeArray = time.split(":");
      if (!isNaN(parseInt(timeArray[0]))) hours = parseInt(timeArray[0]);
      if (!isNaN(parseInt(timeArray[1]))) minutes = parseInt(timeArray[1]);
      if (!isNaN(parseInt(timeArray[2]))) seconds = parseInt(timeArray[2]);
    }
    return { hours, minutes, seconds };
  })(props.item.time);

  return (
    <>
      <span>{props.item.date}</span>
      <span>{`${time.hours < 10 ? "0" : ""}${time.hours}:${
        time.minutes < 10 ? "0" : ""
      }${time.minutes}:${time.seconds < 10 ? "0" : ""}${time.seconds}`}</span>
      <label>{props.item.comment}</label>
    </>
  );
};

const FlowTimeLine: FC<{
  label: string;
  value: string;
  onChange: (time: string) => void;
}> = (props) => {
  const splittedTime = props.value.split(":");
  const hours = splittedTime[0];
  const minutes = splittedTime[1];
  const seconds = splittedTime[2];

  return (
    <div className="flex gap-2 w-full items-center">
      <span className="w-48">{props.label}</span>
      <span className="w-24">{props.value}</span>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex gap-4 w-full">
          <div className="flex flex-1 gap-1 items-center">
            <input
              className="flex-1"
              type="range"
              min={0}
              max={23}
              defaultValue={0}
              value={hours}
              onChange={(e) =>
                props.onChange(
                  `${normalize(e.target.value)}:${minutes}:${seconds}`
                )
              }
            />
            <label>{hours}h</label>
          </div>
          <div className="flex flex-1 gap-1 items-center">
            <input
              className="flex-1"
              type="range"
              min={0}
              max={59}
              defaultValue={0}
              value={minutes}
              onChange={(e) =>
                props.onChange(
                  `${hours}:${normalize(e.target.value)}:${seconds}`
                )
              }
            />
            <label>{minutes}m</label>
          </div>
          <div className="flex flex-1 gap-1 items-center">
            <input
              className="flex-1"
              type="range"
              min={0}
              max={59}
              defaultValue={0}
              value={seconds}
              onChange={(e) =>
                props.onChange(
                  `${hours}:${minutes}:${normalize(e.target.value)}`
                )
              }
            />
            <label>{seconds}s</label>
          </div>
        </div>
      </div>
    </div>
  );
};

const SlaTimeInput: FC<{ value: number; onChange: (time: number) => void }> = ({
  value,
  onChange,
}) => {
  const daysRef = useRef<HTMLInputElement>(null);
  const hoursRef = useRef<HTMLInputElement>(null);

  const days = Math.floor(value / 24);
  const hours = value % 24;

  function setTime() {
    const totalHours =
      parseInt(daysRef.current!.value) * 24 + parseInt(hoursRef.current!.value);
    onChange(totalHours);
  }

  return (
    <div className="flex gap-2 w-full items-center">
      <span className="w-48">SLA tid:</span>
      <span className="w-24">{normalize(value.toString())} timer</span>
      <div className="flex gap-2 w-full">
        <div className="flex flex-1 gap-1 items-center">
          <input
            ref={daysRef}
            className="flex-1"
            type="range"
            min={0}
            max={30}
            defaultValue={0}
            value={days}
            onChange={setTime}
          />
          <label className="w-16">{normalize(days.toString())} dager</label>
        </div>
        <div className="flex flex-1 gap-1 items-center">
          <input
            ref={hoursRef}
            className="flex-1"
            type="range"
            min={0}
            max={23}
            defaultValue={0}
            value={hours}
            onChange={setTime}
          />
          <label className="w-16">{normalize(hours.toString())} timer</label>
        </div>
      </div>
    </div>
  );
};
